/**
 * Sync client. Local-first: every write lands in IndexedDB immediately and
 * is queued in the outbox; this engine pushes the outbox to the relay and
 * pulls anything the paired device wrote, all end-to-end encrypted.
 *
 * Relay contract (see worker/):
 *   PUT  /pairs/:pairId                    → ensure pair exists
 *   POST /pairs/:pairId/events             → { events: WireEvent[], device }
 *                                            ← { ok, latest }
 *   GET  /pairs/:pairId/events?since=N&not=device
 *                                            ← { events: WireEventOut[], latest }
 */
import { outboxAll, outboxDeleteMatching, outboxPut, kvSet, type OutboxEntry } from './db';
import { decryptJson, deriveKey, encryptJson } from './crypto';
import { nowIso } from './time';
import type { PairingState, SyncPayload, WireEventOut } from './types';

export const DEFAULT_SYNC_URL: string =
  (import.meta.env.VITE_SYNC_URL as string | undefined) ?? 'http://localhost:8787';

export type SyncStatus = 'idle' | 'syncing' | 'offline' | 'unpaired' | 'error';

interface SyncCallbacks {
  /** Apply payloads that arrived from the paired device. */
  onRemote: (payloads: SyncPayload[]) => Promise<void>;
  onStatus?: (status: SyncStatus) => void;
  /**
   * The relay no longer knows the pair (404), e.g. after the retention
   * wipe. The pairId is self-authenticating, so the handler may safely
   * re-register the pair and re-queue local history; the engine retries
   * the sync once afterwards.
   */
  onPairLost?: () => Promise<void>;
}

const POLL_MS = 30_000;
/** Stay well under the relay's 500-events-per-request cap. */
const PUSH_BATCH = 200;
/** Mirror of the relay's per-blob cap (base64 chars = bytes). */
const MAX_BLOB_CHARS = 32 * 1024;

/**
 * Monotonic outbox revision. Seeded from the clock so revisions keep
 * increasing across reloads; queuedAt alone is too coarse (whole seconds)
 * to tell a re-queued entry apart from the snapshot a push captured.
 */
let outboxRev = Date.now();
function nextOutboxRev(): number {
  outboxRev += 1;
  return outboxRev;
}

class HttpError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export class SyncEngine {
  private pairing: PairingState | null = null;
  private key: CryptoKey | null = null;
  private deviceId = '';
  private timer: ReturnType<typeof setInterval> | null = null;
  private running = false;
  /** Set when work arrives while a sync is running, so the loop goes again. */
  private dirty = false;
  private callbacks: SyncCallbacks;

  constructor(callbacks: SyncCallbacks) {
    this.callbacks = callbacks;
  }

  /** (Re)configure for the current pairing; pass null when unpaired. */
  async configure(pairing: PairingState | null, deviceId: string): Promise<void> {
    this.pairing = pairing;
    this.deviceId = deviceId;
    this.key = pairing ? await deriveKey(pairing.code) : null;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (pairing) {
      this.timer = setInterval(() => void this.syncNow(), POLL_MS);
      document.addEventListener('visibilitychange', this.onVisibility);
      void this.syncNow();
    } else {
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.callbacks.onStatus?.('unpaired');
    }
  }

  private onVisibility = () => {
    if (document.visibilityState === 'visible') void this.syncNow();
  };

  /** Queue a payload for the paired device. Safe to call when unpaired. */
  async queue(id: string, payload: SyncPayload): Promise<void> {
    await outboxPut({ id, payload, queuedAt: nowIso(), rev: nextOutboxRev() });
    if (this.pairing) void this.syncNow();
  }

  async syncNow(): Promise<void> {
    if (!this.pairing || !this.key) return;
    if (this.running) {
      // A sync is in flight; make it loop once more so nothing queued
      // during the round-trip is left behind.
      this.dirty = true;
      return;
    }
    this.running = true;
    this.callbacks.onStatus?.('syncing');
    try {
      let recovered = false;
      do {
        this.dirty = false;
        try {
          let pushError: HttpError | null = null;
          try {
            await this.push();
          } catch (err) {
            // A push rejected by the relay (4xx other than 404) must not
            // block pulling the partner's data; report it afterwards.
            if (err instanceof HttpError && err.status !== 404 && err.status < 500) {
              pushError = err;
            } else {
              throw err;
            }
          }
          await this.pull();
          if (pushError) throw pushError;
        } catch (err) {
          if (
            err instanceof HttpError &&
            err.status === 404 &&
            !recovered &&
            this.callbacks.onPairLost
          ) {
            // Relay forgot the pair (retention wipe). Let the app
            // re-register and re-queue, then run the loop once more.
            recovered = true;
            await this.callbacks.onPairLost();
            this.dirty = true;
            continue;
          }
          throw err;
        }
      } while (this.dirty && this.pairing);
      this.callbacks.onStatus?.(this.pairing ? 'idle' : 'unpaired');
    } catch {
      this.callbacks.onStatus?.(navigator.onLine ? 'error' : 'offline');
    } finally {
      this.running = false;
    }
  }

  private base(pairing: PairingState): string {
    return `${pairing.serverUrl.replace(/\/$/, '')}/pairs/${pairing.pairId}`;
  }

  private async push(): Promise<void> {
    const pairing = this.pairing;
    const key = this.key;
    if (!pairing || !key) return;
    const entries = await outboxAll();
    if (entries.length === 0) return;
    // Chunk well under the relay's batch cap; a full-history snapshot can
    // hold weeks of events.
    for (let i = 0; i < entries.length; i += PUSH_BATCH) {
      await this.pushBatch(pairing, key, entries.slice(i, i + PUSH_BATCH));
      // Unpaired or re-paired mid-flight: stop touching the outbox.
      if (this.pairing !== pairing) return;
    }
  }

  private async pushBatch(
    pairing: PairingState,
    key: CryptoKey,
    batch: OutboxEntry[],
  ): Promise<void> {
    const sendable: { entry: OutboxEntry; blob: string }[] = [];
    const dead: OutboxEntry[] = [];
    for (const e of batch) {
      const blob = await encryptJson(key, e.payload);
      // The relay hard-rejects oversized blobs; dead-letter the entry (the
      // data stays local) instead of wedging sync forever on one payload.
      if (blob.length > MAX_BLOB_CHARS) dead.push(e);
      else sendable.push({ entry: e, blob });
    }
    if (sendable.length > 0) {
      const res = await fetch(`${this.base(pairing)}/events`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          events: sendable.map((s) => ({ id: s.entry.id, blob: s.blob })),
          device: this.deviceId,
        }),
      });
      if (!res.ok) throw new HttpError(res.status, `push failed: ${res.status}`);
    }
    if (this.pairing !== pairing) return;
    // Delete only rows whose rev still matches this snapshot: anything
    // re-queued during the round-trip keeps its newer rev and goes out on
    // the next loop instead of being silently dropped.
    await outboxDeleteMatching(
      [...sendable.map((s) => s.entry), ...dead].map((e) => ({ id: e.id, rev: e.rev })),
    );
  }

  private async pull(): Promise<void> {
    const pairing = this.pairing;
    const key = this.key;
    if (!pairing || !key) return;
    const since = pairing.cursor;
    const res = await fetch(
      `${this.base(pairing)}/events?since=${since}&not=${encodeURIComponent(this.deviceId)}`,
    );
    if (!res.ok) throw new HttpError(res.status, `pull failed: ${res.status}`);
    const data = (await res.json()) as { events: WireEventOut[]; latest: number };
    if (data.events.length > 0) {
      const payloads: SyncPayload[] = [];
      for (const e of data.events) {
        try {
          payloads.push(await decryptJson<SyncPayload>(key, e.blob));
        } catch {
          // Undecryptable blob (e.g. from a stale pairing) — skip it.
        }
      }
      if (payloads.length > 0) await this.callbacks.onRemote(payloads);
    }
    // Bail out if configure() swapped or cleared the pairing mid-flight:
    // this pull's cursor must never land in a different pairing's state.
    if (this.pairing !== pairing) return;
    if (data.latest !== since) {
      this.pairing = { ...pairing, cursor: data.latest };
      await kvSet('pairing', this.pairing);
    }
  }
}

/** Ensure the pair exists on the relay (called by the inviting device). */
export async function registerPair(serverUrl: string, pairId: string): Promise<void> {
  const res = await fetch(`${serverUrl.replace(/\/$/, '')}/pairs/${pairId}`, { method: 'PUT' });
  if (!res.ok) throw new Error(`pair registration failed: ${res.status}`);
}

/**
 * True when the relay already knows this pair, false on 404. The joining
 * device verifies instead of registering, so a typoed code surfaces an
 * error rather than silently creating a brand-new empty pair.
 */
export async function verifyPair(serverUrl: string, pairId: string): Promise<boolean> {
  const res = await fetch(
    `${serverUrl.replace(/\/$/, '')}/pairs/${pairId}/events?since=${Number.MAX_SAFE_INTEGER}`,
  );
  if (res.status === 404) return false;
  if (!res.ok) throw new Error(`pair lookup failed: ${res.status}`);
  return true;
}
